
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  toRefs,
  ref,
} from "vue";
import { DatasetRecord } from "@/types";
import {
  useDataSource,
  useGammaData,
  useGroupedRecords,
  useLayout,
} from "@/services";

interface LocalState {
  group: string;
  isAssignDisabled: boolean;
  currentSelection: string;
}
export default defineComponent({
  name: "GroupsController",
  props: {
    selection: {
      type: Array as () => DatasetRecord[],
      default: () => [],
    },
  },
  setup(props) {
    const store = useGroupedRecords();
    onMounted(() => {
      useLayout().isLeftSidebarOpen = false;
    });
    const isNewGroup = ref(false);
    const showErrorMsg = ref(false);
    const state: LocalState = reactive({
      group: computed({
        get: () => store.selectedGroup,
        set: (value) => {
          store.selectedGroup = value;
        },
      }),
      isAssignDisabled: computed(
        () => !state.group?.length || !props.selection.length
      ),
      currentSelection: computed(() =>
        props.selection.map((val) => val.uniqueIdentifier).join(", ")
      ),
    });
    const addNewGroup = async () => {
      await store.groupsGetNewGroupId();
      isNewGroup.value = true;
    };
    const removeFromGroup = () => {
      if (props.selection.length) {
        Promise.all(
          props.selection.map((r) =>
            useGammaData().updateRecord(r.updateGroupQuery("0") || {}, r.lcId)
          )
        ).then(() => {
          useDataSource(useGammaData().activeSourceId)().fetchData({
            filter: [],
          });
        });
      }
    };

    const addToGroup = async () => {
      if (!store.groupsSet.size) await store.fetchGroupsList();
      const hasBoundaries = store.groupsSet.has(state.group);
      const hasPoliciesAssigned = await store.groupHasPolicies(
        state.group,
        useDataSource(useGammaData().activeSourceId)().catalogUri
      );
      if (isNewGroup.value || hasPoliciesAssigned || hasBoundaries) {
        for (let selection of props.selection) {
          await useGammaData().updateRecord(
            selection.updateGroupQuery(store.selectedGroup) || {},
            selection.lcId
          );
        }
        await useDataSource(useGammaData().activeSourceId)().fetchData();
      } else {
        showErrorMsg.value = true;
      }
    };

    const preventNonNumeric = (event) => {
      if (event.ctrlKey || event.metaKey) {
        return;
      }
      if (
        !/^[0-9]$/.test(event.key) &&
        event.key !== "Backspace" &&
        event.key !== "ArrowLeft" &&
        event.key !== "ArrowRight" &&
        event.key !== "Delete"
      ) {
        event.preventDefault();
      } else {
        isNewGroup.value = false;
      }
    };

    const handlePaste = (event) => {
      event.preventDefault();
      const pastedData = event.clipboardData?.getData("text") || "";
      state.group = pastedData.replace(/\D/g, "");
    };

    return {
      ...toRefs(state),
      addNewGroup,
      removeFromGroup,
      addToGroup,
      state,
      preventNonNumeric,
      showErrorMsg,
      handlePaste,
    };
  },
  watch: {
    selection(data) {
      useGroupedRecords().selectedRecords = data;
    },
  },
});
